import { useCallback, useEffect, useState } from 'react';
import { DEFAULT_PAGE_SIZE, initialPagination } from 'utils/pagination';

export const usePaginatedList = ({
  fetchPage,
  getItems,
  enabled = true,
  resetDeps = [],
  per = DEFAULT_PAGE_SIZE,
}) => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [inProcess, setInProcess] = useState(false);
  const [pagination, setPagination] = useState(initialPagination);
  const resetKey = JSON.stringify(resetDeps);

  useEffect(() => {
    setPage(1);
    setItems([]);
  }, [resetKey]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    let isCancelled = false;

    (async () => {
      setInProcess(true);
      try {
        const response = await fetchPage({ page, per });
        const data = response.data;

        if (isCancelled) {
          return;
        }

        const newItems = getItems(data) || [];
        setItems((current) =>
          page === 1 ? newItems : [...current, ...newItems],
        );
        setPagination(data.pagination || initialPagination);
      } catch (error) {
        if (!isCancelled) {
          console.error('Failed to fetch paginated list:', error);
        }
      } finally {
        if (!isCancelled) {
          setInProcess(false);
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [enabled, page, per, resetKey]);

  const loadMore = useCallback(() => {
    if (pagination.has_next_page && !inProcess) {
      setPage((currentPage) => currentPage + 1);
    }
  }, [pagination.has_next_page, inProcess]);

  const reset = useCallback(() => {
    setPage(1);
    setItems([]);
  }, []);

  return {
    items,
    loadMore,
    hasNextPage: pagination.has_next_page,
    inProcess,
    isLoadingMore: inProcess && page > 1,
    isLoading: inProcess && page === 1,
    pagination,
    reset,
  };
};
